import { useEffect } from "react";
import { createContext, useContext, useMemo, useState } from "react";
import { Camera, CamerasData } from "@/schemas/camera";
import { usePostGetCamerasList } from "@/api/pizza-checker/camera/camera";
import { useDebounce } from "@/hooks/use-debounce";
import {
  getStoredNumber,
  getStoredString,
  isStoredStateRecord,
  readLocalStorageState,
  StoredStateRecord,
  writeLocalStorageState,
} from "@/hooks/use-local-storage-state";

interface CameraContextType {
  cameras: Camera[] | undefined;
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  setPerPage: (perPage: number) => void;
  totalCameras: number | undefined;
  totalPages: number | undefined;
  deviceIdFilter: string;
  setDeviceIdFilter: (deviceId: string) => void;
  nameFilter: string;
  setNameFilter: (name: string) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortOrder: "asc" | "desc";
  setSortOrder: (sortOrder: "asc" | "desc") => void;
  assignedFilter: boolean | null;
  setAssignedFilter: (assigned: boolean | null) => void;
  refetchCameras: () => void;
}

interface CameraProviderProps {
  children: React.ReactNode;
}

const CameraContext = createContext<CameraContextType | undefined>(undefined);
const CAMERA_FILTER_STORAGE_KEY = "pizza-admin:camera-filters:v1";

type CameraFilterState = {
  perPage: number;
  deviceIdFilter: string;
  nameFilter: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  assignedFilter: boolean | null;
};

const DEFAULT_CAMERA_FILTER_STATE: CameraFilterState = {
  perPage: 10,
  deviceIdFilter: "",
  nameFilter: "",
  sortBy: "name",
  sortOrder: "asc",
  assignedFilter: null,
};

const getStoredSortOrder = (
  record: StoredStateRecord,
  key: string,
  fallback: "asc" | "desc",
) => {
  const value = record[key];
  return value === "asc" || value === "desc" ? value : fallback;
};

const getStoredNullableBoolean = (
  record: StoredStateRecord,
  key: string,
  fallback: boolean | null,
) => {
  const value = record[key];
  return typeof value === "boolean" || value === null ? value : fallback;
};

const readCameraFilterState = (): CameraFilterState => {
  const storedState = readLocalStorageState<StoredStateRecord>(
    CAMERA_FILTER_STORAGE_KEY,
    {},
    (storedValue) => (isStoredStateRecord(storedValue) ? storedValue : {}),
  );
  const perPage = getStoredNumber(
    storedState,
    "perPage",
    DEFAULT_CAMERA_FILTER_STATE.perPage,
  );

  return {
    perPage: perPage > 0 ? perPage : DEFAULT_CAMERA_FILTER_STATE.perPage,
    deviceIdFilter: getStoredString(
      storedState,
      "deviceIdFilter",
      DEFAULT_CAMERA_FILTER_STATE.deviceIdFilter,
    ),
    nameFilter: getStoredString(
      storedState,
      "nameFilter",
      DEFAULT_CAMERA_FILTER_STATE.nameFilter,
    ),
    sortBy: getStoredString(
      storedState,
      "sortBy",
      DEFAULT_CAMERA_FILTER_STATE.sortBy,
    ),
    sortOrder: getStoredSortOrder(
      storedState,
      "sortOrder",
      DEFAULT_CAMERA_FILTER_STATE.sortOrder,
    ),
    assignedFilter: getStoredNullableBoolean(
      storedState,
      "assignedFilter",
      DEFAULT_CAMERA_FILTER_STATE.assignedFilter,
    ),
  };
};

export const CameraProvider = ({ children }: CameraProviderProps) => {
  const initialFilterState = useMemo(readCameraFilterState, []);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(initialFilterState.perPage);
  const [deviceIdFilter, setDeviceIdFilter] = useState(
    initialFilterState.deviceIdFilter,
  );
  const [nameFilter, setNameFilter] = useState(initialFilterState.nameFilter);
  const [sortBy, setSortBy] = useState(initialFilterState.sortBy);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    initialFilterState.sortOrder,
  );
  const [assignedFilter, setAssignedFilter] = useState<boolean | null>(
    initialFilterState.assignedFilter,
  );

  const currentFilterState = useMemo<CameraFilterState>(
    () => ({
      perPage,
      deviceIdFilter,
      nameFilter,
      sortBy,
      sortOrder,
      assignedFilter,
    }),
    [perPage, deviceIdFilter, nameFilter, sortBy, sortOrder, assignedFilter],
  );

  useEffect(() => {
    writeLocalStorageState(CAMERA_FILTER_STORAGE_KEY, currentFilterState);
  }, [currentFilterState]);

  // Debounced filter values
  const debouncedDeviceIdFilter = useDebounce(deviceIdFilter, 400);
  const debouncedNameFilter = useDebounce(nameFilter, 400);

  useEffect(() => {
    setPage(0);
  }, [debouncedDeviceIdFilter, debouncedNameFilter, assignedFilter, sortBy, sortOrder, perPage]);

  const { data, refetch: refetchCameras } = usePostGetCamerasList<CamerasData>(
    {
      page: page,
      page_size: perPage,
      sort_order: sortOrder.toUpperCase(),
      sort_property: sortBy,
    },
    // Keep the connectivity status of the cameras fresh
    { query: { refetchInterval: 30_000 } },
  );

  const { cameras, totalCameras, totalPages } = useMemo(() => {
    return {
      cameras: data?.cameras || [],
      totalCameras: data?.total || 0,
      totalPages: data?.page_size || 0,
    };
  }, [data]);

  const filteredCameras = useMemo(() => {
    if (!cameras) return undefined;

    return cameras.filter(camera => {
      // Filter by device ID
      if (debouncedDeviceIdFilter && !camera.id.toLowerCase().includes(debouncedDeviceIdFilter.toLowerCase())) {
        return false;
      }

      // Filter by name
      if (debouncedNameFilter && !camera.name.toLowerCase().includes(debouncedNameFilter.toLowerCase())) {
        return false;
      }

      // Filter by assigned status
      if (assignedFilter !== null && camera.assigned !== assignedFilter) {
        return false;
      }

      return true;
    });
  }, [cameras, debouncedDeviceIdFilter, debouncedNameFilter, assignedFilter]);

  const value: CameraContextType = {
    cameras: filteredCameras,
    page,
    setPage,
    perPage,
    setPerPage,
    totalCameras,
    totalPages,
    deviceIdFilter,
    setDeviceIdFilter,
    nameFilter,
    setNameFilter,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    assignedFilter,
    setAssignedFilter,
    refetchCameras,
  };

  return (
    <CameraContext.Provider value={value}>
      {children}
    </CameraContext.Provider>
  );
};

export const useCameraContext = () => {
  const context = useContext(CameraContext);
  if (context === undefined) {
    throw new Error("useCameraContext must be used within a CameraProvider");
  }
  return context;
};
